import { Alert, Linking, Platform } from "react-native";

export type LocationPermissionStatus =
  | "granted"
  | "denied"
  | "unavailable"
  | "restricted";

export interface LocationPermissionResult {
  status: LocationPermissionStatus;
  coordinates?: { latitude: number; longitude: number };
  error?: string;
}

// Conditionally import expo-location only for non-web platforms
let Location: typeof import("expo-location") | null = null;
if (Platform.OS !== "web") {
  // Using require instead of import to avoid the web platform issue
  Location = require("expo-location");
}

export class LocationPermissionHandler {
  private static isWeb = Platform.OS === "web";

  private static async handleWebLocation(): Promise<LocationPermissionResult> {
    if (!("geolocation" in navigator)) {
      return {
        status: "unavailable",
        error: "Geolocation is not supported",
      };
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            status: "granted",
            coordinates: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
          });
        },
        (error) => {
          let status: LocationPermissionStatus = "denied";
          if (error.code === error.PERMISSION_DENIED) {
            status = "denied";
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            status = "unavailable";
          } else {
            status = "restricted";
          }

          resolve({
            status,
            error: error.message,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    });
  }

  private static async handleNativeLocation(): Promise<LocationPermissionResult> {
    if (!Location) {
      return {
        status: "unavailable",
        error: "Location services not available",
      };
    }

    try {
      const serviceEnabled = await Location.hasServicesEnabledAsync();
      if (!serviceEnabled) {
        return {
          status: "unavailable",
          error: "Location services are disabled",
        };
      }

      let { status } = await Location.getForegroundPermissionsAsync();

      if (status !== "granted") {
        const { status: newStatus } =
          await Location.requestForegroundPermissionsAsync();
        status = newStatus;
      }

      if (status === "granted") {
        const location = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.High,
        });

        return {
          status: "granted",
          coordinates: {
            latitude: location.coords.latitude,
            longitude: location.coords.longitude,
          },
        };
      }

      return {
        status: status as LocationPermissionStatus,
        error: "Permission not granted",
      };
    } catch (error) {
      return {
        status: "unavailable",
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  static async getCurrentLocation(): Promise<LocationPermissionResult> {
    if (this.isWeb) {
      return this.handleWebLocation();
    }

    const permissionResult = await this.handleNativeLocation();

    if (permissionResult.status !== "granted") {
      Alert.alert(
        "Location Access Required",
        "Please enable location services to continue",
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Open Settings",
            onPress: () => {
              if (this.isWeb) {
                window.open("chrome://settings/content/location");
              } else {
                Linking.openSettings();
              }
            },
          },
        ]
      );
      return permissionResult;
    }

    try {
      if (this.isWeb) {
        return this.handleWebLocation();
      }

      if (!Location) {
        throw new Error("Location services not available");
      }

      const location = await Location.getCurrentPositionAsync({});
      return {
        status: "granted",
        coordinates: {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
        },
      };
    } catch (error) {
      Alert.alert(
        "Location Error",
        "Unable to get your current location. Please try again.",
        [{ text: "OK" }]
      );
      return {
        status: "unavailable",
        error: "Failed to get current location",
      };
    }
  }
}
